import { useState, useRef } from "react";
import { FeeDue } from "../../feeDue/types";
import { getLoggedInUserRole } from "../../../modules/common/user/UserService";
import { UserRole } from "../../../modules/common/shared/types";
import { Fee, TuitionPaymentRequest } from "../types";
import { validateAdminPayment } from "../validators/feePaymentValidation";
import { initiateStudentPayment, payStudentFee } from "../services/feePaymentService";
import { useNavigate } from "react-router-dom";
import { KEEP_LOCKED, useSubmitAction } from "../../../components/common/form/useSubmitAction";
import { appCache } from "../../../cache/appCache";

interface UseFeePaymentProps {
  feeDue: FeeDue;
}

export const useFeePayment = ({ feeDue }: UseFeePaymentProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactionIdRequired, setTransactionIdRequired] = useState(false);
  const [currentYearDiscountDisabled, setCurrentYearDiscountDisabled] = useState(feeDue.previousTotalDue > 0);

  const transactionIdRef = useRef<HTMLInputElement>(null);
  const previousYDiscountRef = useRef<HTMLInputElement>(null);
  const currentYearDiscountRef = useRef<HTMLInputElement>(null);
  const amountPayingRef = useRef<HTMLInputElement>(null);
  const paymentButtonRef = useRef<HTMLButtonElement>(null);

  const balanceAmount = feeDue?.payable + feeDue?.previousTotalDue;

  const updateCurrentYDiscountDisability = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    source: "PREVIOUS_YEARS_DUE_INPUT" | "AMOUNT_PAYING_INPUT",
  ) => {
    setError(null);

    const previousYDiscount = Number(previousYDiscountRef.current?.value ?? 0);
    const amountPaying = Number(amountPayingRef.current?.value ?? 0);
    const previousTotalDue = feeDue?.previousTotalDue ?? 0;
    let canDisableCurrentYDiscount = amountPaying + previousYDiscount >= previousTotalDue;
    canDisableCurrentYDiscount = !canDisableCurrentYDiscount;

    if (source === "PREVIOUS_YEARS_DUE_INPUT") {
      setCurrentYearDiscountDisabled(canDisableCurrentYDiscount);
    } else if (source === "AMOUNT_PAYING_INPUT") {
    } else {
      throw error;
    }

    if (canDisableCurrentYDiscount) {
      if (currentYearDiscountRef.current) {
        currentYearDiscountRef.current.value = "";
        currentYearDiscountRef.current.disabled = true;
      }
      setCurrentYearDiscountDisabled(true);
    } else {
      setCurrentYearDiscountDisabled(false);
    }
  };

  const buildAdminFeePayload = (formData: FormData): Fee => {
    return {
      studentName: feeDue.studentName,
      admissionNo: feeDue.admissionNo,
      gradeName: feeDue.gradeName,
      sectionName: feeDue.sectionName,
      amount: Number(formData.get("amountPaying")),
      transactionId: transactionIdRequired ? (formData.get("transactionId")?.toString() ?? "") : "",
      dateOfPayment: formData.get("dateOfPayment")?.toString() ?? "",
      paymentMode: !transactionIdRequired ? "CASH" : (formData.get("paymentType")?.toString() ?? "CASH"),
      discount: undefined,
      terms: undefined,
      receiptNo: undefined,
      payable: feeDue.payable,
      balance: 0,
      collectedBy: "",
      gradeTerm: undefined,
      previousYDiscount: Number(formData.get("previousYDiscount") ?? 0),
      currentYDiscount: Number(formData.get("currentYDiscount") ?? 0),
      previousYearDues: feeDue.previousTotalDue,
    };
  };

  const buildStudentFeePayload = (formData: FormData): TuitionPaymentRequest => {
    return {
      admissionNo: feeDue.admissionNo,
      amount: Number(formData.get("amountPaying")),
    };
  };

  // double-click guard (shared): the lock is held until the payment call ends; kept on the
  // gateway redirect so the button can't be clicked again while the page is leaving
  const submitPayment = async (e: React.FormEvent<HTMLFormElement>) => {
    setError(null);
    setLoading(true);
    const formData = new FormData(e.currentTarget);
    const role = getLoggedInUserRole();

    try {
      if (Number(formData.get("amountPaying")) <= 0) {
        setError("Amount should be greater than 0");
        setLoading(false);
        return;
      }
      if (role === UserRole.ADMIN) {
        const previousYDiscount = Number(formData.get("previousYDiscount") ?? 0);
        const currentYDiscount = Number(formData.get("currentYDiscount") ?? 0);
        const amountPaying = Number(formData.get("amountPaying") ?? 0);

        const validation = validateAdminPayment(previousYDiscount, currentYDiscount, amountPaying, balanceAmount, feeDue.previousTotalDue);

        if (!validation.valid) {
          setError(validation.message);
          setLoading(false);
          return;
        }

        const feeData = buildAdminFeePayload(formData);
        const result = await payStudentFee(feeData);
        console.log(result.receiptNo);
        // fee totals changed: the dashboard refetches on its next visit
        appCache.invalidate("dashboard");
        navigate("/app/fees/pay/success", {
          state: { receiptNo: result.receiptNo, gradeName: feeDue.gradeName, admissionNo: feeDue.admissionNo },
        });
      } else {
        const feeData = buildStudentFeePayload(formData);
        const paymentUrl = await initiateStudentPayment(feeData);
        console.log(paymentUrl);
        window.location.href = paymentUrl;
        return KEEP_LOCKED;
      }
    } catch (err) {
      setError("Payment failed. Please try again or contact Admin.");
    } finally {
      setLoading(false);
    }
  };
  const { run: initiatePayment, submitting } = useSubmitAction(submitPayment);

  return {
    loading,
    error,
    balanceAmount,
    transactionIdRequired,
    setTransactionIdRequired,
    currentYearDiscountDisabled,
    updateCurrentYDiscountDisability,
    initiatePayment,
    submitting,
    // refs
    transactionIdRef,
    previousYDiscountRef,
    currentYearDiscountRef,
    amountPayingRef,
    paymentButtonRef,
  };
};
